import { currencySymbolMap } from "../constants";
import { DiscountType, PaymentType } from "../models";
import { AppUser } from "../pages/api";
import { DMath } from "./DMath";

/** `withSplit` for forms that can take a split payment. */
export const getPaymentTypeOptions = (t: any, withSplit: boolean = false) => [
  { value: PaymentType.CASH, label: t("CASH") },
  { value: PaymentType.CARD, label: t("CARD") },
  { value: PaymentType.UPI, label: t("UPI") },
  { value: PaymentType.CHEQUE, label: t("CHEQUE") },
  { value: PaymentType.BANK, label: t("BANK") },
  { value: PaymentType.CREDIT, label: t("CREDIT") },
  ...(withSplit ? [{ value: PaymentType.SPLIT, label: t("SPLIT") }] : []),
];

export const getDiscountTypeOptions = (t: any) => [
  { value: DiscountType.PERCENTAGE, label: t("Percentage") },
  { value: DiscountType.FLAT, label: t("Flat") },
];

export const toErrorMsg = (error: any) => {
  if (error) {
    try {
      if (Array.isArray(error)) {
        return error.join(", ");
      } else if (typeof error === "object") {
        const payload = error?.data || error?.error || error;
        const candidates = [
          payload?.message,
          payload?.error_user_msg,
          payload?.metaError?.message,
          payload?.metaError?.error_user_msg,
          payload?.error?.message,
          payload?.error?.error_user_msg,
          error?.message,
          error?.error_user_msg,
        ];

        const message = candidates.find(
          (item) => typeof item === "string" && item.trim().length > 0,
        );
        if (message) {
          return message;
        }

        if (payload && typeof payload === "object") {
          const nested = payload.metaError || payload.error || payload.data;
          if (nested && typeof nested === "object") {
            const nestedMessage = [
              nested.message,
              nested.error_user_msg,
              nested.error?.message,
              nested.error?.error_user_msg,
            ].find(
              (item) => typeof item === "string" && item.trim().length > 0,
            );
            if (nestedMessage) {
              return nestedMessage;
            }
          }
        }

        // Extract string values recursively so we never show "[object Object]".
        const extractStrings = (value: any): Array<string> => {
          if (value === null || value === undefined) return [];
          if (typeof value === "string") return [value];
          if (typeof value === "number" || typeof value === "boolean") {
            return [String(value)];
          }
          if (Array.isArray(value)) {
            return value.flatMap((item) => extractStrings(item));
          }
          if (typeof value === "object") {
            return Object.values(value).flatMap((item) => extractStrings(item));
          }
          return [];
        };

        const extracted = extractStrings(payload).filter(
          (item) => item && item.trim().length > 0,
        );
        const joined = extracted.join(", ");
        if (joined) {
          return joined;
        }
        // Never display the raw "[object Object]".
        try {
          const serialized = JSON.stringify(payload);
          if (serialized && serialized !== "{}") {
            return serialized;
          }
        } catch {
          // ignore serialization errors
        }
        return "";
      } else {
        return error;
      }
    } catch (ex) {
      return "";
    }
  } else {
    return "";
  }
};

export const readMsgFromObject = (obj: any) => {
  if (typeof obj === "string") {
    return obj.toString();
  } else if (Object.keys(obj).length > 0) {
    let msg = "";
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === "string") {
        msg += `${msg.length > 0 ? ", " : ""}${obj[key]}`;
      } else if (Object.keys(obj[key]).length > 0) {
        Object.keys(obj[key]).forEach((key2) => {
          msg += `${msg.length > 0 ? ", " : ""}${obj[key][key2]}`;
        });
      }
    });
    return msg;
  }
  return "";
};

export const numberFieldOnFocus = (e: any, props: any) => {
  if (e.target.value && DMath.eq(e.target.value, "0")) {
    props.setFieldValue(e.target.name, "");
  }
};

export const numberFieldOnBlur = (e: any, props: any) => {
  if (!e.target.value || e.target.value.length < 1) {
    props.setFieldValue(e.target.name, 0);
  }
};

export const getSubscription = (appUser: AppUser) => {
  let subscription: any = {};
  if (appUser?.user?.client) {
    if (
      appUser.user?.client?.isBranch &&
      appUser.user?.client?.parent &&
      appUser.user?.client?.parent?.subscription
    ) {
      subscription = appUser.user?.client?.parent?.subscription;
    } else if (appUser.user?.client?.subscription) {
      subscription = appUser.user?.client?.subscription;
    }
  }
  return subscription;
};

// export const getCurrentSubscription = (appUser: AppUser) => {
//   let subscription: any = getSubscription(appUser);
//   return subscription && process.env.REACT_APP_APP
//     ? subscription[process.env.REACT_APP_APP]
//     : null;
// };

export const getValue = (obj: any, path: string) => {
  if (!path) return undefined;
  if (!path.includes(".")) return obj?.[path];
  return path.split(".").reduce((o, key) => o?.[key], obj);
};

export const getDefaultValue = (input: any, defaultValue: any = "") => {
  return input ? input : defaultValue;
};

export const getTemplate = (appUser: AppUser) =>
  appUser?.user?.currentClient?.billTemplate ||
  appUser?.user?.client?.billTemplate ||
  "template1";

export const getCurrency = (appUser: AppUser) =>
  appUser?.user?.currentClient?.currency ||
  appUser?.user?.client?.currency ||
  "";

export const getCurrencySymbol = (appUser: AppUser) => {
  const currency = getCurrency(appUser);
  return currency ? currencySymbolMap[currency] : "";
};

export const getLogo = (appUser: AppUser) => {
  return (
    appUser?.user?.currentClient?.logo || appUser?.user?.client?.logo || ""
  );
};

export const tEn = (t: any, key: string, options = {}) =>
  t(key, { ...options, lng: "en" });

export const splitBillNumber = (input: string) => {
  const arr = input?.split(":");
  if (arr?.length === 3) {
    return {
      ...(arr[0].length ? { financialYear: arr[0] } : {}),
      ...(arr[1]?.length ? { billPrefix: arr[1] } : {}),
      ...(arr[2]?.length ? { billNumber: arr[2] } : {}),
    };
  } else if (arr?.length === 2) {
    return {
      ...(arr[0].length === 9 && arr[0].indexOf("-") > 0
        ? { financialYear: arr[0] }
        : arr[0].length > 0
          ? { billPrefix: arr[0] }
          : {}),
      ...(arr[1].length ? { billNumber: arr[1] } : {}),
    };
  } else if (arr?.length === 1 && arr[0].length > 0) {
    return { billNumber: arr[0] };
  } else {
    return {};
  }
};

export const formatBarcodeString = (type: string, data: any) =>
  `${type}:${data.financialYear ? `${data.financialYear}:` : ""}${data.billPrefix ? `${data.billPrefix}:` : ""}${data?.billNumber}`;
export const noop = () => {
  /**/
};

export const removeQueryParams = (query: string, keysToRemove: string[]) => {
  if (query && keysToRemove) {
    const params = new URLSearchParams(query);
    keysToRemove.forEach((key) => params.delete(key));
    const result = params.toString();
    return result ? `?${result}` : "";
  } else {
    return query;
  }
};

export const updateQueryParams = (query: string, key: string, value: any) => {
  if (query && key) {
    const params = new URLSearchParams(query);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    const result = params.toString();
    return result ? `?${result}` : "";
  } else {
    return query;
  }
};

export const getPaymentTypeColor = (paymentType: string) => {
  if (paymentType) {
    switch (paymentType) {
      case PaymentType.CASH:
        return "success";
      case PaymentType.CARD:
        return "secondary";
      case PaymentType.UPI:
        return "warning";
      case PaymentType.CHEQUE:
        return "info";
      case PaymentType.BANK:
        return "primary";
      case PaymentType.CREDIT:
        return "error";
      default:
        return "default";
    }
  } else {
    return "default";
  }
};
export const isValidUrl = (str: string) => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

export const isValidJSONObject = (str: string) => {
  try {
    const value = JSON.parse(str);
    return value !== null && typeof value === "object" && !Array.isArray(value);
  } catch {
    return false;
  }
};

export const toBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  const normalized = String(value).trim().toLowerCase();
  return ["true", "yes", "1"].includes(normalized);
};
